/**
 * Messages between this app's open windows.
 *
 * With "Open in a new window" on, one person may have four windows open. Some
 * things must happen in all of them at once: signing out, locking, choosing a
 * farm. A BroadcastChannel reaches every window of this site in this browser
 * profile and never leaves the computer.
 */
export type WindowMessage =
  | { type: 'signed-out' }
  | { type: 'signed-in' }
  | { type: 'lock' }
  | { type: 'unlock' }
  | { type: 'activity'; at: number }
  | { type: 'farm'; farmIds: string[] }
  | { type: 'company' }
  /** The outbox changed: work saved, sent or refused (.docs/023). */
  | { type: 'outbox' };

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('poultry.app');

const handlers = new Set<(message: WindowMessage) => void>();

channel?.addEventListener('message', (e: MessageEvent<WindowMessage>) => {
  for (const handler of handlers) handler(e.data);
});

/** Tells every other window. The sending window does not receive it. */
export function tellOtherWindows(message: WindowMessage): void {
  channel?.postMessage(message);
}

export function onWindowMessage(handler: (message: WindowMessage) => void): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}
